import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  UtensilsCrossed,
  Map,
  Bell,
  MoreVertical,
  Settings as SettingsIcon,
  GanttChart,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useAuth } from '../../contexts/AuthContext';

// Primary items shown on mobile bottom nav
const primaryNavigation = [
  { 
    name: 'Αρχική', 
    href: '/', 
    icon: LayoutDashboard,
  },
  { 
    name: 'Κρατήσεις', 
    href: '/reservations', 
    icon: Calendar,
  },
  {
    name: 'Ειδοποιήσεις',
    href: '/notifications',
    icon: Bell,
  },
];

// Secondary items moved to "More" menu on mobile
const secondaryNavigation = [
  { 
    name: 'Τραπέζια', 
    href: '/tables', 
    icon: UtensilsCrossed,
  },
  { 
    name: 'Πελάτες', 
    href: '/customers', 
    icon: Users,
  },
  { 
    name: 'Χάρτης', 
    href: '/floor-plan', 
    icon: Map,
  },
  {
    name: 'Timeline',
    href: '/timeline',
    icon: GanttChart,
  },
  {
    name: 'Ρυθμίσεις',
    href: '/settings',
    icon: SettingsIcon,
  },
];

const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const { user } = useAuth();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMoreMenuOpen]);

  // Close menu when navigating
  const handleNavClick = () => {
    setIsMoreMenuOpen(false);
  };

  return (
    <div
      className="box-content fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 md:hidden bottom-nav"
      style={{ 
        paddingBottom: 'max(0.5rem, calc(env(safe-area-inset-bottom, 0px) - 19px))' 
      }}
    >
      <nav className="flex items-center justify-around px-1 pt-2">
        {/* Primary Navigation */}
        {primaryNavigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className={cn(
                'relative flex flex-col items-center justify-center px-3 min-w-0 flex-1',
                'mobile-touch-feedback',
                isActive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <div className="relative mb-1">
                <Icon
                  className={cn(
                    'h-6 w-6 transition-colors',
                    isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                  )}
                />
                {item.href === '/notifications' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium truncate leading-tight',
                  isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                )}
              >
                {item.name}
              </span>
            </NavLink>
          );
        })}

        {/* More Menu Button */}
        <div className="relative flex-1">
          <button
            onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
            className={cn(
              'relative w-full flex flex-col items-center justify-center px-3',
              'mobile-touch-feedback',
              isMoreMenuOpen
                ? 'text-green-600 dark:text-green-400'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title="Περισσότερα"
          >
            <MoreVertical
              className={cn(
                'h-6 w-6 mb-1 transition-colors',
                isMoreMenuOpen ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
              )}
            />
            <span className={cn(
              'text-[10px] font-medium truncate leading-tight',
              isMoreMenuOpen ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
            )}>
              Περισσότερα
            </span>
          </button>

          {/* More Menu Dropdown */}
          {isMoreMenuOpen && (
            <div
              ref={menuRef}
              className="absolute bottom-full right-0 mb-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50 min-w-[220px]"
            >
              {secondaryNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-border/50 last:border-b-0',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </NavLink>
                );
              })}

              {user?.email && (
                <div className="border-t border-border/60 px-4 py-3 text-xs text-muted-foreground bg-card/80">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mb-1">
                    Συνδεδεμένος ως
                  </div>
                  <div className="font-medium text-foreground truncate">
                    {user.email}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default BottomNavigation;
